import { useState, useMemo, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { ArrowRight, Clock, MapPin, Route, X } from "lucide-react";
import api from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLang } from "@/lib/i18n";
import useDocumentMeta from "@/lib/useDocumentMeta";
import { Reveal } from "@/lib/reveal";

const SITE_URL = "https://juketransfers.hr";

const DESTINATIONS = [
  {
    slug: "dubrovnik",
    name: "Dubrovnik",
    tagline: "The pearl of the Adriatic",
    duration: "~3h drive",
    distance: "230 km",
    price_from: 380,
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/6/67/The_walls_of_the_fortress_and_View_of_the_old_city._panorama.jpg/1920px-The_walls_of_the_fortress_and_View_of_the_old_city._panorama.jpg",
    description:
      "The medieval walled city, UNESCO World Heritage since 1979. Marble streets polished by centuries, Baroque palaces, and a sea-fortress skyline you've probably already seen on screen. Spend the day inside the walls, then linger on Banje Beach as the sun drops behind Lokrum.",
    activities: [
      "Walk the Old City walls (2 km circuit, best at golden hour)",
      "Cable-car ride up Mount Srđ for the definitive view",
      "Boat trip to Lokrum Island — botanical garden + peacocks",
      "Game of Thrones filming-locations tour",
      "Dinner in a stone-vaulted courtyard restaurant off Stradun",
    ],
  },
  {
    slug: "plitvice",
    name: "Plitvice Lakes",
    tagline: "16 turquoise lakes, waterfalls everywhere",
    duration: "~3h drive",
    distance: "245 km",
    price_from: 340,
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/3/39/View_in_Plitvice_Lakes_National_Park.jpg/1920px-View_in_Plitvice_Lakes_National_Park.jpg",
    description:
      "Croatia's oldest national park and its most photographed landscape — sixteen crystalline lakes cascading through beech and fir forest via hundreds of waterfalls. Wooden boardwalks skim across the water so you feel suspended above the milky-turquoise pools.",
    activities: [
      "Full loop (route C or H) — 4–6 h of wooden boardwalks",
      "Electric boat crossing Lake Kozjak (included in ticket)",
      "Big Waterfall (Veliki Slap, 78 m) at the lower lakes",
      "Local trout lunch at a mill-house restaurant nearby",
      "Sunset viewpoint from Vidikovac",
    ],
  },
  {
    slug: "hvar",
    name: "Hvar Island Gateway (Split Port)",
    short_name: "Hvar",
    tagline: "Airport → Split Ferry Port — catch your catamaran to Hvar",
    duration: "~35 min drive",
    distance: "25 km",
    price_from: 45,
    // Uses the Split Airport ↔ Split Ferry Port fixed route (no premium fee).
    route_from: "Split Airport",
    route_to: "Split Ferry Port",
    premium: false,
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/27/View_of_Hvar_02.jpg/3840px-View_of_Hvar_02.jpg",
    description:
      "Premium private transfer from Split Airport directly to the Split Ferry Port, where you can easily catch your catamaran or ferry to Hvar. Our driver meets you inside arrivals, handles the luggage, and delivers you to the correct berth in about 35 minutes so you can step straight onto your island crossing.",
    activities: [
      "Meet & greet inside Split Airport arrivals hall",
      "Direct 25 km airport-to-port drive (~35 min)",
      "Luggage assistance to the ferry terminal",
      "Real-time ferry timing coordination on request",
      "Return leg from Split Port ↔ Airport also available",
    ],
  },
  {
    slug: "krka",
    name: "Krka National Park",
    tagline: "Waterfalls you can (sometimes) swim beside",
    duration: "~1h 20m drive",
    distance: "88 km",
    price_from: 160,
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/3/37/Krkawatervallen.jpg/3840px-Krkawatervallen.jpg",
    description:
      "The Krka river descends through seven waterfalls before spilling into the Adriatic — most famously at Skradinski Buk, a 46-metre-wide travertine cascade. Combine easy wooden trails, a Franciscan monastery on an islet, and (in shoulder season) picnic spots directly next to the falls.",
    activities: [
      "Skradinski Buk boardwalks — the postcard waterfall",
      "Boat ride up to Visovac monastery islet",
      "Roški Slap upper falls — quieter, equally stunning",
      "Etno-village mill museum",
      "Konoba lunch of Krka-river trout",
    ],
  },
  {
    slug: "zadar",
    name: "Zadar",
    tagline: "Sea organ · Sun Salutation · Roman ruins",
    duration: "~1h 30m drive",
    distance: "158 km",
    price_from: 220,
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/1d/Zadar-Sea-Organ.jpg/1920px-Zadar-Sea-Organ.jpg",
    description:
      "Old-town Zadar sits on a compact peninsula wrapped by 3 000 years of history — Roman forum, Byzantine churches, Venetian walls. Then, on the promenade, Nikola Bašić's Sea Organ plays music through wave-driven pipes and the Greeting to the Sun disc lights up at dusk.",
    activities: [
      "Sunset at the Sea Organ — Alfred Hitchcock called it the world's best",
      "Roman Forum + St Donatus rotunda",
      "Maraschino cherry-liqueur tasting (invented here)",
      "Day-trip out to Kornati archipelago",
      "Fish market breakfast, then coffee on Kalelarga",
    ],
  },
  {
    slug: "sibenik",
    name: "Šibenik",
    tagline: "Fortress hilltops + a UNESCO cathedral",
    duration: "~1h drive",
    distance: "88 km",
    price_from: 130,
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/4b/%C5%A0ibenik_BW_2024-09-30_12-11-31.jpg/3840px-%C5%A0ibenik_BW_2024-09-30_12-11-31.jpg",
    description:
      "Often skipped for its bigger neighbours — a mistake. Šibenik's stone alleyways climb four hilltop fortresses (three restored) and its 15th-century Cathedral of St James is a UNESCO gem carved entirely from stone, no mortar. Great base for Krka in the morning + swimming afternoons.",
    activities: [
      "Cathedral of St James — carved lion portal + 71 human heads frieze",
      "St Michael's Fortress open-air stage (summer concerts)",
      "Barone Fortress + interactive medieval AR experience",
      "Boat out to Zlarin — Croatia's coral island",
      "Wine cellars in the Prokljan lake region",
    ],
  },
  {
    slug: "makarska",
    name: "Makarska",
    tagline: "White pebble beaches under Biokovo mountain",
    duration: "~1h drive",
    distance: "63 km",
    price_from: 110,
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/d/d5/Urbanisti%C4%8Dka_cjelina_Makarske.jpg/3840px-Urbanisti%C4%8Dka_cjelina_Makarske.jpg",
    description:
      "The Makarska Riviera stretches ~60 km along the Adriatic, backed by the dramatic Biokovo mountain that plunges straight into turquoise sea. White pebble beaches (Punta Rata in Brela is on the world's top-10 lists), pine groves, and a lively promenade lined with konobas.",
    activities: [
      "Skywalk Biokovo — glass platform 1 228 m above the sea",
      "Punta Rata beach in Brela at sunrise",
      "Kayaking along the coast to Baška Voda",
      "Franciscan Monastery + Malacology (shell) Museum",
      "Evening ćevapi + Dalmatian wine on the riva",
    ],
  },
  {
    slug: "omis",
    name: "Omiš",
    tagline: "Pirate town at the mouth of Cetina canyon",
    duration: "~40 min drive",
    distance: "27 km",
    price_from: 65,
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/14/Cetina_river.jpg/3840px-Cetina_river.jpg",
    description:
      "Once the base of the fearsome Omiš pirates who took on the Venetian navy — today the adventure capital of Dalmatia. The Cetina river cuts a limestone canyon just meters wide, opening into a beach at the town centre. Bring shoes you can swim in.",
    activities: [
      "White-water rafting the Cetina canyon (grade II–III, family-safe)",
      "Ziplining across the gorge (8 lines, 2 100 m total)",
      "Hike up to Mirabella & Starigrad fortresses",
      "Sea kayaking under the pirate cliffs",
      "Konoba dinner over frog-legs and river trout",
    ],
  },
  {
    slug: "zrce",
    name: "Zrće Beach",
    tagline: "Croatia's Ibiza — sunrise beach parties",
    duration: "~3h drive",
    distance: "260 km",
    price_from: 400,
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/a6/It%27s_Oh_So_Quiet_%2823240529535%29.jpg/1920px-It%27s_Oh_So_Quiet_%2823240529535%29.jpg",
    description:
      "White pebble crescent on the island of Pag, three open-air super-clubs (Aquarius, Papaya, Noa) running from mid-morning till dawn, and a festival lineup — Sonus, Hideout, Fresh Island — that draws 100 000+ every summer. The party doesn't sleep; neither does our driver on the return.",
    activities: [
      "Sundown to sunrise across Aquarius → Papaya → Noa",
      "Daytime pool club at Papaya (yes, at 11 AM)",
      "Boat parties from Zrće to Rucica bay",
      "Bike to the town of Novalja for lamb and Pag cheese",
      "Nova beach for the calm hangover morning",
    ],
  },
  {
    slug: "klis",
    name: "Klis Fortress",
    tagline: "Meereen, King's Landing's neighbour — and 2 000 years of history",
    duration: "~30 min drive",
    distance: "14 km",
    price_from: 45,
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/56/Klis_iz_zraka.jpg/1920px-Klis_iz_zraka.jpg",
    description:
      "Perched on a saddle between two mountain ridges just above Split, Klis has guarded the inland pass since Illyrian times. Roman garrison, Croatian medieval seat, Ottoman stronghold — and, more recently, Meereen in Game of Thrones. On a clear day you see the coast from Trogir to Brač.",
    activities: [
      "Full fortress walk with panorama across Split",
      "Explore three levels of ramparts + underground cisterns",
      "Traditional spit-roast lamb at Konoba Perlica across the road",
      "Continue up to Sveti Jure summit on Mosor (hikers)",
      "Combine with a Salona (Roman Solin) morning",
    ],
  },
];

// Anchor coordinates — hard-coded so we skip the Google geocode on the pickup
// side; drop-off coords stay empty because Google Autocomplete on the booking
// form will re-populate them when the customer confirms the address.
const SPLIT_ORIGIN = { name: "Split", lat: 43.5081, lon: 16.4402 };

export default function DestinationsPage() {
  const { t } = useLang();
  const navigate = useNavigate();
  const { slug: urlSlug } = useParams();
  const [openSlug, setOpenSlug] = useState(urlSlug || null);
  const [livePrices, setLivePrices] = useState({}); // { [slug]: number }
  const open = useMemo(() => DESTINATIONS.find((d) => d.slug === openSlug), [openSlug]);

  // Keep the modal open state in lock-step with the URL slug so the user can
  // land directly on /destinations/dubrovnik AND close the dialog by clicking
  // the backdrop (which navigates back to /destinations).
  useEffect(() => {
    setOpenSlug(urlSlug || null);
  }, [urlSlug]);

  // Per-destination SEO metadata (only fires when a slug is in the URL).
  const openPrice = open ? (livePrices[open.slug] || open.price_from) : null;
  useDocumentMeta(open ? {
    title: `${open.short_name || open.name} Transfer from Split · From €${Number(openPrice).toFixed(0)} · Juke Transfers`,
    description:
      `Premium private transfer from Split to ${open.short_name || open.name}. ` +
      `Prices from €${Number(openPrice).toFixed(0)}. ${open.duration}, ${open.distance}. ${open.tagline}. ` +
      `Book a chauffeured comfort-class ride online in 60 seconds.`,
    canonical: `${SITE_URL}/destinations/${open.slug}`,
    ogImage: open.image,
    ogType: "article",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "TouristTrip",
      "name": `Split to ${open.short_name || open.name} — Private Transfer`,
      "description": open.description,
      "image": open.image,
      "url": `${SITE_URL}/destinations/${open.slug}`,
      "provider": {
        "@type": "LocalBusiness",
        "name": "Juke Transfers",
        "url": SITE_URL,
        "areaServed": "Split, Croatia",
      },
      "offers": {
        "@type": "Offer",
        "price": livePrices[open.slug] || open.price_from,
        "priceCurrency": "EUR",
        "availability": "https://schema.org/InStock",
      },
    },
  } : (() => {
    const priceVals = Object.values(livePrices).filter((v) => v > 0);
    const fromPrice = priceVals.length
      ? Math.min(...priceVals)
      : Math.min(...DESTINATIONS.map((d) => d.price_from));
    return {
      title: `Destinations from Split — 10 Curated Day Trips from €${Number(fromPrice).toFixed(0)} · Juke Transfers`,
      description:
        `Chauffeured private transfers from Split to Dubrovnik, Plitvice Lakes, ` +
        `Krka waterfalls, Zadar, Šibenik, Makarska, Omiš, Hvar, Zrće and Klis. ` +
        `Prices from €${Number(fromPrice).toFixed(0)}. Live pricing, one-click booking.`,
      canonical: `${SITE_URL}/destinations`,
      ogImage: DESTINATIONS[0]?.image,
      ogType: "website",
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "itemListElement": DESTINATIONS.map((d, i) => ({
          "@type": "ListItem",
          "position": i + 1,
          "url": `${SITE_URL}/destinations/${d.slug}`,
          "name": d.short_name || d.name,
        })),
      },
    };
  })());

  // Fetch live prices from the admin-managed catalog (single call, no N+1).
  // Falls back to the marketing `price_from` if no match is found.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/pricing/catalog");
        const routes = data?.routes || [];
        const norm = (s) => (s || "")
          .toLowerCase()
          .replace(/[čć]/g, "c").replace(/š/g, "s")
          .replace(/ž/g, "z").replace(/đ/g, "d")
          .replace(/[^a-z0-9]+/g, " ").trim();
        const next = {};
        for (const d of DESTINATIONS) {
          // Match against `route_to` when set (handles cases like Hvar
          // where the destination card's name differs from the actual
          // route endpoint stored in admin, e.g. "Split Ferry Port").
          const wantTo = norm(d.route_to || d.short_name || d.name);
          const match = routes.find((r) => {
            const rTo = norm(r.dropoff);
            return rTo === wantTo || rTo.includes(wantTo) || wantTo.includes(rTo);
          });
          if (match) next[d.slug] = Number(match.base_price_eur) || null;
        }
        if (!cancelled) setLivePrices(next);
      } catch {
        // Ignore — hardcoded `price_from` values remain visible.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const bookRoute = (dest) => {
    const from = dest.route_from || SPLIT_ORIGIN.name;
    const to = dest.route_to || dest.name;
    const params = new URLSearchParams({ from, to });
    if (dest.premium !== false) params.set("premium", "1");
    navigate(`/?${params.toString()}#book`);
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col">
      <Header />

      {/* Hero */}
      <section className="relative pt-40 pb-20 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#C5A059] mb-4">
            {t("dest.eyebrow") || "Destinations"}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white font-light tracking-tight max-w-4xl">
            Ten unforgettable trips, one Split doorstep.
          </h1>
          <p className="mt-6 text-zinc-400 max-w-2xl text-base leading-relaxed">
            From island lavender fields to UNESCO waterfalls and the medieval walls of Dubrovnik —
            hand-picked destinations we drive to daily, in our chauffeured comfort class vehicles.
            Pick a stop, we&rsquo;ll handle the rest.
          </p>
        </div>
      </section>

      {/* Grid */}
      <section className="flex-1 px-6 lg:px-10 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {DESTINATIONS.map((d, i) => (
            <Reveal key={d.slug} delay={(i % 6) * 60}>
            <button
              type="button"
              data-testid={`destination-card-${d.slug}`}
              onClick={() => navigate(`/destinations/${d.slug}`)}
              className="group relative aspect-[4/5] w-full rounded-xl overflow-hidden border border-white/10 hover:border-[#C5A059]/50 text-left transition-all duration-500 focus:outline-none focus:ring-2 focus:ring-[#C5A059]/50 hover:-translate-y-0.5"
            >
              <img
                src={d.image}
                alt={d.name}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.06]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
              <div className="absolute inset-0 p-6 flex flex-col justify-end">
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#C5A059] mb-2 flex items-center gap-3">
                  <Clock className="w-3 h-3" /> {d.duration}
                  <span className="text-zinc-500">·</span>
                  <Route className="w-3 h-3" /> {d.distance}
                </p>
                <h3 className="font-display text-3xl text-white font-light tracking-tight">{d.short_name || d.name}</h3>
                <p className="mt-2 text-sm text-zinc-300 line-clamp-2">{d.tagline}</p>
                <div className="mt-4 flex items-baseline justify-between">
                  <p className="text-[#C5A059] text-sm font-medium tabular-nums">
                    {livePrices[d.slug] ? `€${livePrices[d.slug].toFixed(0)}` : `from €${d.price_from}`}
                  </p>
                  <span className="text-white text-xs uppercase tracking-widest flex items-center gap-1 opacity-70 group-hover:opacity-100 transition-opacity">
                    View <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </div>
            </button>
            </Reveal>
          ))}
        </div>
      </section>

      <Footer />

      {/* Detail dialog */}
      <Dialog open={!!openSlug} onOpenChange={(v) => { if (!v) navigate("/destinations"); }}>
        <DialogContent
          data-testid="destination-detail-dialog"
          className="bg-[#0a0a0a] border-white/10 text-zinc-100 w-[calc(100%-1rem)] sm:w-full max-w-3xl p-0 rounded-2xl overflow-hidden max-h-[92vh] overflow-y-auto grid-cols-[minmax(0,1fr)] [&>button.absolute]:hidden"
        >
          {open && (
            <>
              <div className="relative h-44 sm:h-64 w-full shrink-0">
                <img
                  src={open.image}
                  alt={open.name}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/40 to-transparent" />
                <button
                  type="button"
                  onClick={() => navigate("/destinations")}
                  aria-label="Close"
                  className="absolute top-3 right-3 h-9 w-9 rounded-full bg-black/70 backdrop-blur border border-white/15 flex items-center justify-center hover:bg-black text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="px-5 py-5 sm:px-8 sm:py-7 space-y-5 sm:space-y-6 min-w-0">
                {/* Meta line — route · duration · distance */}
                <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-[#C5A059] flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="w-3 h-3 shrink-0" />
                    {open.route_from || "Split"} → {open.route_to || open.short_name || open.name}
                  </span>
                  <span className="text-zinc-600">·</span>
                  <span>{open.duration}</span>
                  <span className="text-zinc-600">·</span>
                  <span>{open.distance}</span>
                </p>

                <DialogHeader className="p-0 space-y-2 text-left">
                  <DialogTitle asChild>
                    <h1 className="font-display text-[22px] leading-[1.2] sm:text-4xl sm:leading-tight text-white font-light tracking-tight break-words">
                      Split to {open.short_name || open.name}
                      <span className="block sm:inline text-zinc-400 sm:ml-1">— Private Transfer</span>
                    </h1>
                  </DialogTitle>
                  <DialogDescription className="text-zinc-300 text-[13px] leading-relaxed sm:text-base">
                    {open.tagline}
                  </DialogDescription>
                </DialogHeader>

                <p className="text-[14px] sm:text-base text-zinc-300 leading-relaxed">
                  {open.description}
                </p>

                <div>
                  <p className="text-[11px] uppercase tracking-[0.25em] text-[#C5A059] mb-3">
                    What to do
                  </p>
                  <ul className="space-y-2.5">
                    {open.activities.map((a, i) => (
                      <li key={i} className="text-[13px] sm:text-sm text-zinc-300 leading-relaxed flex gap-3">
                        <span className="text-[#C5A059] shrink-0 mt-0.5">·</span>
                        <span>{a}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Also popular from Split — horizontal carousel of 4 other
                    destinations, tap to open that destination's modal. */}
                {(() => {
                  const others = DESTINATIONS.filter((d) => d.slug !== open.slug).slice(0, 4);
                  if (others.length === 0) return null;
                  return (
                    <div data-testid="also-popular-carousel">
                      <p className="text-[11px] uppercase tracking-[0.25em] text-[#C5A059] mb-3">
                        Also popular from Split
                      </p>
                      <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 -mx-5 sm:-mx-8 px-5 sm:px-8 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                        {others.map((d) => {
                          const price = livePrices[d.slug] || d.price_from;
                          return (
                            <button
                              key={d.slug}
                              type="button"
                              data-testid={`also-popular-${d.slug}`}
                              onClick={() => navigate(`/destinations/${d.slug}`)}
                              className="group snap-start shrink-0 w-36 sm:w-44 rounded-xl overflow-hidden border border-white/10 hover:border-[#C5A059]/60 bg-black/40 transition-all text-left focus:outline-none focus:ring-2 focus:ring-[#C5A059]/50"
                            >
                              <div className="relative aspect-[4/3] overflow-hidden">
                                <img
                                  src={d.image}
                                  alt={d.name}
                                  loading="lazy"
                                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                              </div>
                              <div className="p-2.5">
                                <p className="text-[9px] uppercase tracking-[0.2em] text-zinc-500">
                                  {d.duration}
                                </p>
                                <p className="text-[13px] text-white font-medium mt-0.5 truncate">
                                  {d.short_name || d.name}
                                </p>
                                <p className="text-[#C5A059] text-[11px] font-medium mt-1 tabular-nums">
                                  from €{Number(price).toFixed(0)}
                                </p>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Sticky-bottom price + CTA — sits at the end of the scroll
                  area on mobile so it's always reachable; laid out side-by-side
                  on desktop. */}
              <div className="border-t border-white/10 bg-[#0a0a0a] px-5 py-4 sm:px-8 sm:py-5 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                <div className="flex items-baseline gap-2 sm:flex-col sm:items-start sm:gap-1">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                    From
                  </p>
                  <p
                    data-testid={`modal-price-${open.slug}`}
                    className="text-[#C5A059] text-2xl sm:text-3xl font-light tabular-nums leading-none"
                  >
                    {livePrices[open.slug] ? `€${livePrices[open.slug].toFixed(0)}` : `€${open.price_from}`}
                  </p>
                </div>
                <button
                  type="button"
                  data-testid={`book-transfer-btn-${open.slug}`}
                  onClick={() => bookRoute(open)}
                  className="h-12 px-6 rounded-full bg-[#C5A059] hover:bg-[#D4AF37] text-black text-[11px] uppercase tracking-[0.25em] font-semibold flex items-center justify-center gap-2 w-full sm:w-auto"
                >
                  Book transfer <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
